'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { useImageTransform } from '@/shared/lib/hooks/useImageTransform';

export interface ViewerImage {
  src: string;
  alt?: string;
  /**
   * True when this item is the original angle photo (no render yet) — viewer renders it grayscale
   * to match the small angle card treatment, so users can clearly distinguish unrendered angles.
   */
  isOriginalAngle?: boolean;
}

/** On-screen rect of the card the viewer was opened from (viewport coords). */
export interface ViewerSourceRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Snapshot the on-screen rect of a source card element for the hero morph. */
export function viewerRectFromElement(
  el: HTMLElement | null
): ViewerSourceRect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return null;
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

interface ImageViewerProps {
  /** Single image source (backward compat) */
  src?: string;
  alt?: string;
  /** Multi-image array — takes precedence over src */
  images?: ViewerImage[];
  /** Initial image index for multi-image mode */
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
  /**
   * On-screen rect of the source card the viewer was opened from. When provided,
   * the open/close plays a hero morph: the image scales/translates from this rect
   * to fullscreen and back, with the black backdrop fading in/out — matching the
   * Apple app. Omit (or pass null) to open instantly (no morph).
   */
  sourceRect?: ViewerSourceRect | null;
}

/** Hero morph timing + the card corner radius the window unrolls from. */
const HERO_MS = 300;
const HERO_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const HERO_CARD_RADIUS = 8; // matches the card's `rounded-lg`

interface HeroRect {
  left: number;
  top: number;
  width: number;
  height: number;
  borderRadius: number;
}

export function ImageViewer({
  src,
  alt = 'Image',
  images: imagesProp,
  initialIndex = 0,
  isOpen,
  onClose,
  sourceRect,
}: ImageViewerProps) {
  const t = useTranslations('imageViewer');

  // Normalize to array
  const images: ViewerImage[] =
    imagesProp && imagesProp.length > 0
      ? imagesProp
      : src
        ? [{ src, alt }]
        : [];

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const hasMultiple = images.length > 1;
  const currentImage = images[currentIndex] || images[0];

  const {
    position,
    scale,
    isDragging,
    containerRef,
    setPosition,
    setScale,
    handleMouseDown,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    zoomIn,
    zoomOut,
    resetTransform,
    positionRef,
    scaleRef,
  } = useImageTransform({
    minScale: 0.5,
    maxScale: 5,
    scaleStep: 1.25,
    enableZoom: true,
  });

  const goToPrevious = useCallback(() => {
    if (!hasMultiple) return;
    setCurrentIndex((i) => (i > 0 ? i - 1 : images.length - 1));
    resetTransform();
  }, [hasMultiple, images.length, resetTransform]);

  const goToNext = useCallback(() => {
    if (!hasMultiple) return;
    setCurrentIndex((i) => (i < images.length - 1 ? i + 1 : 0));
    resetTransform();
  }, [hasMultiple, images.length, resetTransform]);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      resetTransform();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          containerRef.current?.focus();
        });
      });
    }
  }, [isOpen, initialIndex]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        // Escape is handled by Radix `onOpenChange` → `requestClose` so it plays
        // the reverse hero morph instead of unmounting instantly.
        case '+':
        case '=':
          zoomIn();
          break;
        case '-':
        case '_':
          zoomOut();
          break;
        case '0':
          resetTransform();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isOpen,
    zoomIn,
    zoomOut,
    resetTransform,
    goToPrevious,
    goToNext,
  ]);

  // Block page zoom and pointer events when viewer is open
  useEffect(() => {
    if (!isOpen) return;

    const preventGestureZoom = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
      }
    };

    const preventPageWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };

    const originalBodyStyle = document.body.style.overflow;
    const originalHtmlStyle = document.documentElement.style.overflow;
    const originalTouchAction = document.documentElement.style.touchAction;
    const originalBodyBackground = document.body.style.backgroundColor;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.touchAction = 'none';
    document.body.style.backgroundColor = 'black';

    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    const originalThemeColor = themeColorMeta?.getAttribute('content') || '';
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', '#000000');
    }

    const viewportMeta = document.querySelector(
      'meta[name="viewport"]'
    ) as HTMLMetaElement | null;
    const originalViewportContent = viewportMeta?.getAttribute('content') || '';

    if (viewportMeta) {
      const newContent = originalViewportContent.replace(
        /user-scalable=yes|user-scalable=1/,
        'user-scalable=no'
      );
      if (!newContent.includes('user-scalable')) {
        viewportMeta.setAttribute('content', newContent + ', user-scalable=no');
      } else {
        viewportMeta.setAttribute('content', newContent);
      }
    }

    document.addEventListener('touchmove', preventGestureZoom, {
      passive: false,
    });
    document.addEventListener('wheel', preventPageWheel, { passive: false });

    return () => {
      document.body.style.overflow = originalBodyStyle;
      document.documentElement.style.overflow = originalHtmlStyle;
      document.documentElement.style.touchAction = originalTouchAction;
      document.body.style.backgroundColor = originalBodyBackground;

      if (themeColorMeta && originalThemeColor) {
        themeColorMeta.setAttribute('content', originalThemeColor);
      }

      if (viewportMeta && originalViewportContent) {
        viewportMeta.setAttribute('content', originalViewportContent);
      }

      document.removeEventListener('touchmove', preventGestureZoom);
      document.removeEventListener('wheel', preventPageWheel);
    };
  }, [isOpen]);

  // Add wheel event listener for image zoom with passive: false
  useEffect(() => {
    if (!isOpen) return;

    let cleanupListeners: (() => void) | undefined;

    const timeoutId = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;

      const wheelHandler = (e: WheelEvent) => {
        if (e.ctrlKey || e.metaKey) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        const containerRect = container.getBoundingClientRect();
        const mouseX = e.clientX - containerRect.width / 2;
        const mouseY = e.clientY - containerRect.height / 2;

        const currentScale = scaleRef.current;
        const currentPosition = positionRef.current;
        const delta = e.deltaY > 0 ? 0.95 : 1.05;
        const newScale = Math.min(Math.max(currentScale * delta, 0.5), 5);

        const pointX = (mouseX - currentPosition.x) / currentScale;
        const pointY = (mouseY - currentPosition.y) / currentScale;

        const newX = mouseX - pointX * newScale;
        const newY = mouseY - pointY * newScale;

        setScale(newScale);
        setPosition({ x: newX, y: newY });
      };

      container.addEventListener('wheel', wheelHandler, { passive: false });
      window.addEventListener('wheel', wheelHandler, { passive: false });

      cleanupListeners = () => {
        container.removeEventListener('wheel', wheelHandler);
        window.removeEventListener('wheel', wheelHandler);
      };
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      cleanupListeners?.();
    };
  }, [
    isOpen,
    containerRef,
    scaleRef,
    positionRef,
    setScale,
    setPosition,
  ]);

  // ---------------------------------------------------------------------------
  // Hero morph (open/close). Mirrors the Apple app: the image flies out of the
  // tapped card to fullscreen and back. The morph window uses `object-cover`, so
  // it reproduces the card's cover-crop exactly at the collapsed end and shows
  // the full image at the expanded end (where the window aspect == image aspect,
  // making cover == contain). The black backdrop only fades — nothing else moves.
  // ---------------------------------------------------------------------------
  const heroEnabled = !!sourceRect;
  // Hero overlay mounted only while morphing; the interactive content (chrome)
  // is faded in once the open morph settles and faded out before the close one.
  const [heroMounted, setHeroMounted] = useState(false);
  const [heroRect, setHeroRect] = useState<HeroRect | null>(null);
  const [chromeVisible, setChromeVisible] = useState(!heroEnabled);
  const [backdropOpaque, setBackdropOpaque] = useState(!heroEnabled);
  // Natural aspect of the morphing image; drives the expanded window. Seeded from
  // the card aspect and refined once the hero <img> decodes (a smooth re-ease).
  const heroAspectRef = useRef<number | null>(null);
  // 'open' | 'close' — guards the async load refinement and double-close.
  const heroDirRef = useRef<'open' | 'close'>('open');
  const heroTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heroRafRef = useRef<number | null>(null);

  // Fullscreen rect the morph expands to — the displayed image's `.fit` box,
  // matching where the interactive content actually paints.
  const computeExpandedRect = useCallback(
    (aspect: number): HeroRect => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let w = vw;
      let h = w / aspect;
      if (h > vh) {
        h = vh;
        w = h * aspect;
      }
      return {
        left: (vw - w) / 2,
        top: (vh - h) / 2,
        width: w,
        height: h,
        borderRadius: 0,
      };
    },
    []
  );

  const collapsedRect = useCallback(
    (rect: ViewerSourceRect): HeroRect => ({
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      borderRadius: HERO_CARD_RADIUS,
    }),
    []
  );

  const expandHero = useCallback(() => {
    if (!sourceRect) return;
    const aspect =
      heroAspectRef.current ?? sourceRect.width / sourceRect.height;
    setHeroRect(computeExpandedRect(aspect));
  }, [sourceRect, computeExpandedRect]);

  const clearHeroTimers = useCallback(() => {
    if (heroTimerRef.current) clearTimeout(heroTimerRef.current);
    if (heroRafRef.current) cancelAnimationFrame(heroRafRef.current);
    heroTimerRef.current = null;
    heroRafRef.current = null;
  }, []);

  // Play the OPEN morph whenever the viewer opens with a source rect.
  useEffect(() => {
    if (!isOpen) return;
    if (!heroEnabled || !sourceRect) {
      setChromeVisible(true);
      setBackdropOpaque(true);
      setHeroMounted(false);
      return;
    }
    heroDirRef.current = 'open';
    heroAspectRef.current = null;
    clearHeroTimers();
    // Start collapsed over the card, page still visible behind (backdrop clear).
    setChromeVisible(false);
    setBackdropOpaque(false);
    setHeroMounted(true);
    setHeroRect(collapsedRect(sourceRect));
    // Next frame → expand + fade the backdrop in.
    heroRafRef.current = requestAnimationFrame(() => {
      heroRafRef.current = requestAnimationFrame(() => {
        setBackdropOpaque(true);
        expandHero();
      });
    });
    // Hand off to the interactive content once the morph lands. Reveal the
    // chrome FIRST (instantly, underneath the still-opaque hero — identical
    // pixels and geometry, so it's invisible), then unmount the hero one frame
    // later. Doing both at once left a ~1-frame gap where the hero was gone but
    // the chrome hadn't painted yet — the post-open flicker the user saw.
    heroTimerRef.current = setTimeout(() => {
      setChromeVisible(true);
      heroRafRef.current = requestAnimationFrame(() => {
        heroRafRef.current = requestAnimationFrame(() => {
          setHeroMounted(false);
        });
      });
    }, HERO_MS);
    return clearHeroTimers;
  }, [isOpen]);

  // Intercept close: play the reverse morph, THEN tell the parent to unmount.
  const requestClose = useCallback(() => {
    if (!heroEnabled || !sourceRect) {
      onClose();
      return;
    }
    if (heroDirRef.current === 'close') return;
    heroDirRef.current = 'close';
    clearHeroTimers();
    resetTransform();
    // Mount the hero at the expanded position (matching the current view), hide
    // the chrome, then collapse back to the card on the next frame.
    setChromeVisible(false);
    setHeroMounted(true);
    expandHero();
    heroRafRef.current = requestAnimationFrame(() => {
      heroRafRef.current = requestAnimationFrame(() => {
        setBackdropOpaque(false);
        setHeroRect(collapsedRect(sourceRect));
      });
    });
    heroTimerRef.current = setTimeout(() => {
      onClose();
    }, HERO_MS);
  }, [
    heroEnabled,
    sourceRect,
    onClose,
    resetTransform,
    clearHeroTimers,
    expandHero,
    collapsedRect,
  ]);

  // Reset hero bookkeeping when fully closed so the next open starts clean.
  useEffect(() => {
    if (isOpen) return;
    clearHeroTimers();
    setHeroMounted(false);
    setChromeVisible(!heroEnabled);
    setBackdropOpaque(!heroEnabled);
    heroDirRef.current = 'open';
  }, [isOpen]);

  const onHeroImgLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      if (!img.naturalWidth || !img.naturalHeight) return;
      heroAspectRef.current = img.naturalWidth / img.naturalHeight;
      // Refine the expanded target mid-open once the true aspect is known.
      if (heroDirRef.current === 'open' && backdropOpaque) expandHero();
    },
    [backdropOpaque, expandHero]
  );

  if (images.length === 0) return null;

  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => !open && requestClose()}
    >
      <DialogPrimitive.Portal>
        {/* Animated black backdrop — fades in/out so the hero morph plays over
            the live page (the image flying out of the card) before the screen
            goes fully black. Instant (no transition) when there's no hero. */}
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black"
          style={{
            width: '100vw',
            height: '100vh',
            minHeight: '-webkit-fill-available',
            opacity: backdropOpaque ? 1 : 0,
            transition: heroEnabled
              ? `opacity ${HERO_MS}ms ${HERO_EASING}`
              : 'none',
          }}
        />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50"
          style={{
            width: '100vw',
            height: '100vh',
            minHeight: '-webkit-fill-available',
            maxWidth: '100vw',
          }}
        >
          <DialogPrimitive.Title className="sr-only">
            {currentImage?.alt || alt || 'Image viewer'}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Use scroll to zoom, drag to pan, arrow keys to navigate, and ESC to
            close
          </DialogPrimitive.Description>
          <div
            ref={containerRef}
            className="relative w-full h-full flex items-center justify-center outline-none overflow-hidden"
            tabIndex={0}
            style={{
              pointerEvents: 'auto',
              touchAction: 'none',
            }}
            onWheel={(e) => {
              e.stopPropagation();
            }}
          >
            {/* Hero morph overlay — mounted only while opening/closing. The image
                uses `object-cover`, so at the collapsed (card) end it reproduces
                the card's cover-crop exactly, and at the expanded end (window
                aspect == image aspect) it shows the full image. */}
            {heroEnabled && heroMounted && heroRect && (
              <div
                className="absolute z-20 overflow-hidden pointer-events-none"
                style={{
                  left: heroRect.left,
                  top: heroRect.top,
                  width: heroRect.width,
                  height: heroRect.height,
                  borderRadius: heroRect.borderRadius,
                  transition: `left ${HERO_MS}ms ${HERO_EASING}, top ${HERO_MS}ms ${HERO_EASING}, width ${HERO_MS}ms ${HERO_EASING}, height ${HERO_MS}ms ${HERO_EASING}, border-radius ${HERO_MS}ms ${HERO_EASING}`,
                }}
              >
                <img
                  src={currentImage?.src}
                  alt=""
                  onLoad={onHeroImgLoad}
                  className={`w-full h-full object-cover ${currentImage?.isOriginalAngle ? 'saturate-[0.25] brightness-95' : ''}`}
                  draggable={false}
                />
              </div>
            )}

            {/* Interactive content (chrome) — revealed instantly under the still-
                opaque hero once the open morph lands (matching geometry/pixels,
                so the swap is seamless), hidden again before the close morph.
                A fade here dipped brightness during the handoff → flicker. */}
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                opacity: chromeVisible ? 1 : 0,
                pointerEvents: chromeVisible ? 'auto' : 'none',
              }}
              onClick={(e) => {
                if (e.target === e.currentTarget) requestClose();
              }}
            >
              {/* Controls - bottom center on mobile, top-right on desktop */}
            <div className="fixed bottom-0 left-1/2 -translate-x-1/2 md:bottom-auto md:top-4 md:left-auto md:right-4 md:translate-x-0 z-10 flex items-center gap-1 bg-black/90 backdrop-blur-sm md:rounded-lg p-1 mb-1 md:mb-0">
              <Button
                    variant="ghost"
                    size="icon"
                    onClick={zoomOut}
                    title="Zoom out (-)"
                    className="h-9 w-9 text-white hover:bg-white/20"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-form-label min-w-[60px] text-center px-2 text-white">
                    {Math.round(scale * 100)}%
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={zoomIn}
                    title="Zoom in (+)"
                    className="h-9 w-9 text-white hover:bg-white/20"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={resetTransform}
                    title="Reset zoom (0)"
                    className="h-9 w-9 text-white hover:bg-white/20"
                  >
                <Maximize2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={requestClose}
                title="Close (ESC)"
                className="h-9 w-9 text-white hover:bg-white/20"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Page counter - top center */}
            {hasMultiple && (
              <div className="fixed top-4 left-1/2 -translate-x-1/2 z-10 text-white/80 text-list-item-subtitle bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">
                {currentIndex + 1} / {images.length}
              </div>
            )}

            {/* Previous button */}
            {hasMultiple && (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="fixed start-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 text-white/70 hover:text-white hover:bg-white/20 rounded-full"
              >
                <ChevronLeft className="h-6 w-6 rtl:-scale-x-100" />
              </Button>
            )}

            {/* Next button */}
            {hasMultiple && (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                className="fixed end-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 text-white/70 hover:text-white hover:bg-white/20 rounded-full"
              >
                <ChevronRight className="h-6 w-6 rtl:-scale-x-100" />
              </Button>
            )}

            {/* Help text - desktop only */}
            <div className="hidden md:block fixed bottom-4 start-4 text-white/60 text-list-item-subtitle bg-black/50 backdrop-blur-sm px-3 py-1 rounded">
              {hasMultiple ? t('helpTextMulti') : t('helpText')}
            </div>

            {/* Content area — one single mode: the image, always zoomable */}
            <div
              className="relative overflow-hidden cursor-move select-none"
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transition: isDragging ? 'none' : 'transform 0.1s ease-out',
              }}
            >
              <img
                src={currentImage?.src}
                alt={currentImage?.alt || alt}
                className={`max-w-[100vw] max-h-[100vh] object-contain ${currentImage?.isOriginalAngle ? 'saturate-[0.25] brightness-95' : ''}`}
                draggable={false}
                fetchPriority="high"
                loading="eager"
              />
            </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

interface ImageWithViewerProps {
  src: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  priority?: boolean;
}

export function ImageWithViewer({
  src,
  alt = 'Image',
  className = '',
  style,
  priority = false,
}: ImageWithViewerProps) {
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  if (!src) {
    return null;
  }

  return (
    <>
      <img
        src={src}
        alt={alt}
        className={`cursor-pointer hover:opacity-90 transition-opacity ${className}`}
        style={style}
        onClick={() => setIsViewerOpen(true)}
        fetchPriority={priority ? 'high' : 'auto'}
        loading={priority ? 'eager' : 'lazy'}
      />
      <ImageViewer
        src={src}
        alt={alt}
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
      />
    </>
  );
}
